import type {
	FeatureContribution,
	Observation,
	VerificationAnchoredPass,
} from "@sitescene/types";

import { type Column, DataTable } from "@/components/data-table";

interface AnchoredObservationsProps {
	/**
	 * What each feature added to the score. Omit to leave the Log LR column
	 * out, for a pass that was never scored.
	 */
	contributions?: FeatureContribution[];
	pass: VerificationAnchoredPass;
}

/**
 * The anchored pass feature by feature: which way each discriminating
 * feature pointed, how clearly, and the model's note, with what it added to
 * the score when there was one. Observations the parser rejected are counted
 * underneath, with why.
 */
export const AnchoredObservations = ({
	contributions,
	pass,
}: AnchoredObservationsProps) => {
	const byFeature = new Map(
		(contributions ?? []).map((f) => [f.feature, f] as const),
	);
	const columns: Column<Observation>[] = [
		{
			header: "Feature",
			key: "feature",
			render: (o) => <code className="text-xs">{o.feature}</code>,
		},
		{ header: "Value", key: "value", render: (o) => o.value },
		{ header: "Strength", key: "strength", render: (o) => o.strength },
		...(contributions
			? [
					{
						header: "Log LR",
						key: "lr",
						render: (o: Observation) =>
							formatContribution(byFeature.get(o.feature)),
					},
				]
			: []),
		{ header: "Note", key: "note", render: (o) => o.note ?? "" },
	];
	return (
		<>
			<DataTable
				columns={columns}
				rowKey={(o) => o.feature}
				rows={pass.observations}
			/>
			{pass.dropped.length > 0 && (
				<p className="mt-2 text-xs text-tertiary">
					{pass.dropped.length} observation(s) dropped by the parser:{" "}
					{pass.dropped.map((d) => d.reason).join("; ")}
				</p>
			)}
		</>
	);
};

const formatContribution = (f: FeatureContribution | undefined): string => {
	if (!f) return "—";
	if (f.ignored) return `0 (${f.ignored})`;
	const dampened = f.dampened.toFixed(2);
	return f.dampened !== f.capped
		? `${dampened} (was ${f.capped.toFixed(2)})`
		: dampened;
};
